import { useCallback } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { fileUploadService } from '@/api/fileUploadApi';
import { useUserId } from '@/auth/UserIdContext';
import { useAddImageToLibrary } from '@/hooks/useAddImageToLibrary';

/**
 * Write-back for a completed Firefly edit (ADO-1412). Mirrors the existing
 * manual image-replace contract (`useImageReplace`) exactly so the edited image
 * flows through the SAME reducer + persistence path:
 *
 *   1. upload the edited File to Azure (`fileUploadService.upload`)
 *   2. `request_replace_post_image`  (marks the request outstanding)
 *   3. `report_post_image_replaced`  (carries the new URL; linked by requestId)
 *
 * Both actions share one `requestId`, which is how the reducer links them. The
 * `artifact_id` + `image_index` are captured by the caller at modal-open time,
 * NOT at completion — so a late-completing edit writes back to the originating
 * artifact even if the user has since navigated the panel away (the reducer's
 * `applyImageUpdateDetailed` applies to the snapshot regardless of the visible
 * panel — see bridge/actions/chat.ts).
 *
 * On failure it emits `report_post_image_replace_failed` (same as manual
 * replace) and rethrows so the modal can keep itself open for retry.
 */
export interface FireflyEditWriteBackTarget {
	artifactId: string;
	/** Carousel slide index; omit for single-image artifacts (ads). */
	imageIndex?: number;
}

export function useFireflyEditWriteBack() {
	const bridgeApi = useBridgeApi();
	const userId = useUserId();
	const addImageToLibrary = useAddImageToLibrary(userId);

	return useCallback(
		async (file: File, target: FireflyEditWriteBackTarget): Promise<void> => {
			if (!userId) {
				throw new Error('Cannot save edit: no user id');
			}
			const requestId = crypto.randomUUID();
			const chatId = bridgeApi.user_state?.current_chat_id ?? undefined;
			const { artifactId, imageIndex } = target;

			bridgeApi.sendAction(
				{
					type: 'request_replace_post_image',
					artifact_id: artifactId,
					...(imageIndex !== undefined && { image_index: imageIndex }),
				},
				requestId,
			);

			try {
				const result = await fileUploadService.upload(file, userId);
				addImageToLibrary(result.url, 'firefly');
				bridgeApi.sendAction(
					{
						type: 'report_post_image_replaced',
						artifact_id: artifactId,
						image_url: result.url,
						// The Firefly editor returns an already-composed image; we do
						// not crop it on the way back, so it is not "cropped" here.
						was_cropped: false,
						chat_id: chatId,
						...(imageIndex !== undefined && { image_index: imageIndex }),
					},
					requestId,
				);
			} catch (err) {
				bridgeApi.sendAction(
					{
						type: 'report_post_image_replace_failed',
						artifact_id: artifactId,
						chat_id: chatId,
					},
					requestId,
				);
				throw err;
			}
		},
		[addImageToLibrary, bridgeApi, userId],
	);
}
